import { useEffect, useRef, useState } from "react";

// Parses "+10,000" → { prefix: "+", numeric: 10000, suffix: "" }
// Parses "IFAI 2021" → not numeric, returns raw
function parseValue(raw) {
    if (raw == null) return { type: "raw", value: "" };
    const s = String(raw);
    const m = s.match(/^([^\d-]*)([-+]?\d[\d,.]*)(.*)$/);
    if (!m) return { type: "raw", value: s };
    const numStr = m[2].replace(/,/g, "");
    const numeric = Number(numStr);
    if (Number.isNaN(numeric)) return { type: "raw", value: s };
    return { type: "num", prefix: m[1], numeric, suffix: m[3], hasComma: m[2].includes(",") };
}

function formatNumber(n, hasComma) {
    const rounded = Math.round(n);
    return hasComma ? rounded.toLocaleString("en-US") : String(rounded);
}

export default function AnimatedNumber({ value, duration = 1400, className = "" }) {
    const parsed = parseValue(value);
    const [display, setDisplay] = useState(parsed.type === "num" ? "0" : parsed.value);
    const ref = useRef(null);
    const startedRef = useRef(false);

    useEffect(() => {
        if (parsed.type !== "num" || startedRef.current) return undefined;
        const el = ref.current;
        if (!el) return undefined;
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || startedRef.current) return;
                    startedRef.current = true;
                    const startTs = performance.now();
                    const animate = (ts) => {
                        const p = Math.min(1, (ts - startTs) / duration);
                        // easeOutExpo
                        const eased = p === 1 ? 1 : 1 - Math.pow(2, -10 * p);
                        const current = parsed.numeric * eased;
                        setDisplay(`${parsed.prefix}${formatNumber(current, parsed.hasComma)}${parsed.suffix}`);
                        if (p < 1) requestAnimationFrame(animate);
                    };
                    requestAnimationFrame(animate);
                });
            },
            { threshold: 0.25 },
        );
        obs.observe(el);
        return () => obs.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);

    if (parsed.type !== "num") {
        return <span ref={ref} className={className}>{parsed.value}</span>;
    }
    return <span ref={ref} className={className}>{display}</span>;
}
